import React, { useState, useCallback } from 'react'
import { useStore } from 'reactflow'
import { useTheme } from '@mui/material'
import { useWorkspaceVisibility } from '../contexts/WorkspaceVisibilityContext'

const ROSE      = '#f43f5e'
const ROSE_DARK = '#e11d48'
const ROSE_GLOW = 'rgba(244,63,94,0.28)'
const NODE_SIZE = 44

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('ws-demand-layer-anim')) {
  const el = document.createElement('style')
  el.id = 'ws-demand-layer-anim'
  el.textContent = `
    @keyframes wsDemandDash {
      from { stroke-dashoffset: 22; }
      to   { stroke-dashoffset: 0; }
    }
  `
  document.head.appendChild(el)
}

function PersonIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  )
}

function WorkspaceDemandLayer({ demands, demandNodePositions, onPositionChange, onDemandClick, selectedDemandId }) {
  const transform     = useStore(s => s.transform)
  const nodeInternals = useStore(s => s.nodeInternals)
  const theme         = useTheme()
  const isDark        = theme.palette.mode === 'dark'
  const { demand: demandVisible, labels: showLabel } = useWorkspaceVisibility()
  const [hoveredId, setHoveredId] = useState(null)

  const [tx, ty, zoom] = transform

  const items = React.useMemo(() => {
    if (!demandVisible) return []
    return demands.map(demand => {
      const targetInternal = nodeInternals.get(demand.target_node)
      if (!targetInternal?.positionAbsolute) return null

      const tw = (targetInternal.width  || NODE_SIZE) / 2
      const th = (targetInternal.height || NODE_SIZE) / 2
      const targetCX = targetInternal.positionAbsolute.x + tw
      const targetCY = targetInternal.positionAbsolute.y + th

      const siblings  = demands.filter(d => d.target_node === demand.target_node)
      const idx       = siblings.findIndex(d => d.id === demand.id)
      const total     = siblings.length
      const spreadX   = (idx - (total - 1) / 2) * 84

      const canvasPos = demandNodePositions[demand.id] || {
        x: targetCX + 110 + spreadX,
        y: targetCY + 90,
      }

      return { demand, canvasPos, targetCX, targetCY }
    }).filter(Boolean)
  }, [demands, nodeInternals, demandNodePositions, demandVisible])

  // Drag with click detection (< 5px = click)
  const handleMouseDown = useCallback((demand, canvasPos, e) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()

    const startX  = e.clientX
    const startY  = e.clientY
    const startCX = canvasPos.x
    const startCY = canvasPos.y
    let dragged = false

    const onMove = (mv) => {
      const dx = mv.clientX - startX
      const dy = mv.clientY - startY
      if (!dragged && Math.sqrt(dx * dx + dy * dy) > 5) dragged = true
      if (dragged) {
        onPositionChange(demand.id, {
          x: startCX + dx / zoom,
          y: startCY + dy / zoom,
        })
      }
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      if (!dragged && onDemandClick) onDemandClick(demand.id)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [zoom, onPositionChange, onDemandClick])

  if (!demandVisible || items.length === 0) return null

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5 }}>

      {/* ── Animated arcs (SVG) ── */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        {items.map(({ demand, canvasPos, targetCX, targetCY }) => {
          const fromX = canvasPos.x * zoom + tx
          const fromY = canvasPos.y * zoom + ty
          const toX   = targetCX  * zoom + tx
          const toY   = targetCY  * zoom + ty

          const dx  = toX - fromX
          const dy  = toY - fromY
          const mx  = (fromX + toX) / 2
          const my  = (fromY + toY) / 2
          const cpX = mx - dy * 0.3
          const cpY = my + dx * 0.3
          const d   = `M ${fromX} ${fromY} Q ${cpX} ${cpY} ${toX} ${toY}`

          return (
            <g key={demand.id}>
              <path d={d} stroke={ROSE} strokeWidth={12} fill="none" opacity={0.05} strokeLinecap="round" />
              <path d={d} stroke={ROSE} strokeWidth={5}  fill="none" opacity={0.11} strokeLinecap="round" />
              <path
                d={d}
                stroke={ROSE}
                strokeWidth={1.5}
                fill="none"
                strokeLinecap="round"
                strokeDasharray="7 5"
                opacity={0.7}
                style={{ animation: 'wsDemandDash 0.75s linear infinite' }}
              />
            </g>
          )
        })}
      </svg>

      {/* ── Demand node circles (HTML) ── */}
      {items.map(({ demand, canvasPos }) => {
        const screenX    = canvasPos.x * zoom + tx
        const screenY    = canvasPos.y * zoom + ty
        const size       = Math.max(24, NODE_SIZE * zoom)
        const iconSize   = Math.max(12, 18 * zoom)
        const isHovered  = hoveredId === demand.id
        const isSelected = selectedDemandId === demand.id
        const bgColor    = isDark ? ROSE_DARK : ROSE
        const customerName = demand.name.replace(/^demand\s*/i, 'Customer ')

        return (
          <div
            key={demand.id}
            onMouseDown={e => handleMouseDown(demand, canvasPos, e)}
            onMouseEnter={() => setHoveredId(demand.id)}
            onMouseLeave={() => setHoveredId(null)}
            style={{
              position:        'absolute',
              left:            screenX - size / 2,
              top:             screenY - size / 2,
              width:           size,
              height:          size,
              borderRadius:    '50%',
              backgroundColor: bgColor,
              display:         'flex',
              flexDirection:   'column',
              alignItems:      'center',
              justifyContent:  'center',
              cursor:          'grab',
              pointerEvents:   'all',
              userSelect:      'none',
              boxShadow:       isSelected
                ? `0 0 0 ${Math.max(2, 3 * zoom)}px #fff, 0 0 0 ${Math.max(4, 5 * zoom)}px ${ROSE}, 0 6px 20px ${ROSE_GLOW}`
                : isHovered
                  ? `0 6px 20px ${ROSE_GLOW}, 0 0 0 ${Math.max(2, 3 * zoom)}px ${ROSE}30`
                  : `0 3px 12px ${ROSE_GLOW}`,
              transform:       isHovered || isSelected ? 'scale(1.1)' : 'scale(1)',
              transition:      'transform 0.2s ease, box-shadow 0.2s ease',
              border:          `${Math.max(1, 2 * zoom)}px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.55)'}`,
              zIndex:          isSelected ? 15 : 10,
            }}
          >
            <PersonIcon size={iconSize} />

            {/* Persistent label */}
            {showLabel && zoom > 0.4 && (
              <div style={{
                position:   'absolute',
                top:        '100%',
                left:       '50%',
                transform:  'translateX(-50%)',
                marginTop:  3 * zoom,
                fontSize:   Math.max(8, 10.5 * zoom),
                fontWeight: 700,
                color:      isDark ? '#fda4af' : '#be123c',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                textShadow: isDark
                  ? '0 1px 4px rgba(0,0,0,0.9)'
                  : '0 1px 3px rgba(255,255,255,0.9)',
                letterSpacing: '0.015em',
                pointerEvents: 'none',
              }}>
                {customerName}
              </div>
            )}

            {/* Hover tooltip */}
            {isHovered && (
              <div style={{
                position:        'absolute',
                bottom:          `calc(100% + ${Math.max(6, 8 * zoom)}px)`,
                left:            '50%',
                transform:       'translateX(-50%)',
                padding:         `${Math.max(4, 6 * zoom)}px ${Math.max(8, 10 * zoom)}px`,
                borderRadius:    Math.max(6, 8 * zoom),
                backgroundColor: isDark ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.97)',
                backdropFilter:  'blur(14px)',
                border:          `1px solid ${isDark ? 'rgba(244,63,94,0.2)' : 'rgba(244,63,94,0.12)'}`,
                boxShadow:       '0 6px 24px rgba(0,0,0,0.18)',
                whiteSpace:      'nowrap',
                pointerEvents:   'none',
                zIndex:          30,
                fontSize:        Math.max(9, 11 * zoom),
                fontWeight:      700,
                color:           isDark ? '#fda4af' : '#be123c',
              }}>
                {customerName}
                <div style={{ fontSize: Math.max(8, 10 * zoom), fontWeight: 500, color: isDark ? '#94a3b8' : '#6b7280', marginTop: 2 }}>
                  qty {demand.order_quantity} · every {demand.arrival_interval}d
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default WorkspaceDemandLayer
